import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
  TextareaAutosize,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EventIcon from '@mui/icons-material/Event';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';
import TablePagination from '@mui/material/TablePagination';

interface Loan {
  loanId: string;
  loanReference: string;
  borrower: string;
  bank: string;
  loanType: string;
  totalPrincipalOutstanding: number;
  totalInterestOutstanding: number;
}

interface RepaymentSchedule {
  paymentId: string;
  loan: Loan;
  paymentDueDate: Date;
  principalAmount: number;
  interestAmount: number;
  totalPayment: number;
  status: 'Pending' | 'Paid' | 'Overdue' | 'Failed' | 'Processing' | 'Cancelled' | 'Scheduled' | 'Partial' | 'Reversed' | 'In Review';
  actualPaymentDate: Date | null;
  principalPaid: number;
  interestPaid: number;
  notes: string;
}

const RepaymentSchedule: React.FC = () => {
  const [loans, setLoans] = useState<Loan[]>([]);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [repaymentSchedules, setRepaymentSchedules] = useState<RepaymentSchedule[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'view' | 'record' | 'reschedule'>('view');
  const [selectedPayment, setSelectedPayment] = useState<RepaymentSchedule | null>(null);
  const [searchLoanReference, setSearchLoanReference] = useState('');
  const [searchDueDateFrom, setSearchDueDateFrom] = useState<Date | null>(null);
  const [searchDueDateTo, setSearchDueDateTo] = useState<Date | null>(null);
  const [searchStatus, setSearchStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    actualPaymentDate: null as Date | null,
    principalPaid: '',
    interestPaid: '',
    notes: '',
    newDueDate: null as Date | null,
  });

  useEffect(() => {
    // TODO: Fetch loans from API
    const mockLoans: Loan[] = [
      {
        loanId: 'LN001',
        loanReference: 'REF001',
        borrower: 'Borrower A',
        bank: 'Bank A',
        loanType: 'STL',
        totalPrincipalOutstanding: 1000000.00,
        totalInterestOutstanding: 50000.00,
      },
    ];
    setLoans(mockLoans);

    // TODO: Fetch repayment schedules from API
    const mockSchedules: RepaymentSchedule[] = [
      {
        paymentId: 'PMT001',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-03-01'),
        principalAmount: 100000.00,
        interestAmount: 5000.00,
        totalPayment: 105000.00,
        status: 'Pending',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT002',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-03-15'),
        principalAmount: 120000.00,
        interestAmount: 6000.00,
        totalPayment: 126000.00,
        status: 'Paid',
        actualPaymentDate: new Date('2024-03-16'),
        principalPaid: 120000.00,
        interestPaid: 6000.00,
        notes: '',
      },
      {
        paymentId: 'PMT003',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-04-01'),
        principalAmount: 110000.00,
        interestAmount: 5500.00,
        totalPayment: 115500.00,
        status: 'Overdue',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT004',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-04-15'),
        principalAmount: 90000.00,
        interestAmount: 4500.00,
        totalPayment: 94500.00,
        status: 'Failed',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT005',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-05-01'),
        principalAmount: 95000.00,
        interestAmount: 4750.00,
        totalPayment: 99750.00,
        status: 'Processing',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT006',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-05-15'),
        principalAmount: 105000.00,
        interestAmount: 5250.00,
        totalPayment: 110250.00,
        status: 'Cancelled',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT007',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-06-01'),
        principalAmount: 115000.00,
        interestAmount: 5750.00,
        totalPayment: 120750.00,
        status: 'Scheduled',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT008',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-06-15'),
        principalAmount: 98000.00,
        interestAmount: 4900.00,
        totalPayment: 102900.00,
        status: 'Partial',
        actualPaymentDate: null,
        principalPaid: 50000.00,
        interestPaid: 2500.00,
        notes: '',
      },
      {
        paymentId: 'PMT009',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-07-01'),
        principalAmount: 102000.00,
        interestAmount: 5100.00,
        totalPayment: 107100.00,
        status: 'Reversed',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
      {
        paymentId: 'PMT010',
        loan: mockLoans[0],
        paymentDueDate: new Date('2024-07-15'),
        principalAmount: 108000.00,
        interestAmount: 5400.00,
        totalPayment: 113400.00,
        status: 'In Review',
        actualPaymentDate: null,
        principalPaid: 0,
        interestPaid: 0,
        notes: '',
      },
    ];
    setRepaymentSchedules(mockSchedules);
  }, []);

  const handleOpenDialog = (type: 'view' | 'record' | 'reschedule', payment?: RepaymentSchedule) => {
    setDialogType(type);
    if (payment) {
      setSelectedPayment(payment);
      setFormData({
        actualPaymentDate: payment.actualPaymentDate,
        principalPaid: payment.principalPaid.toString(),
        interestPaid: payment.interestPaid.toString(),
        notes: payment.notes,
        newDueDate: payment.paymentDueDate,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPayment(null);
    setFormData({
      actualPaymentDate: null,
      principalPaid: '',
      interestPaid: '',
      notes: '',
      newDueDate: null,
    });
  };

  const handleSave = () => {
    // TODO: Implement save logic
    handleCloseDialog();
  };

  const handleExportToExcel = () => {
    // TODO: Implement export to Excel logic
  };

  const filteredSchedules = repaymentSchedules.filter(schedule => {
    const matchesLoan = !searchLoanReference || schedule.loan.loanReference === searchLoanReference;
    const matchesStatus = !searchStatus || schedule.status === searchStatus;
    const matchesDateFrom = !searchDueDateFrom || schedule.paymentDueDate >= searchDueDateFrom;
    const matchesDateTo = !searchDueDateTo || schedule.paymentDueDate <= searchDueDateTo;
    return matchesLoan && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const paginatedSchedules = filteredSchedules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ color: '#374151', fontWeight: 600 }}>
            Repayment Schedule
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                bgcolor: '#fff',
                color: '#1E293B',
                borderColor: '#E5E7EB',
                textTransform: 'none',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
              }}
            >
              Export to Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                bgcolor: '#fff',
                color: '#1E293B',
                borderColor: '#E5E7EB',
                textTransform: 'none',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
              }}
            >
              Print
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: 3,
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
          }}
        >
          <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Loan Reference</InputLabel>
            <Select
              value={searchLoanReference}
              label="Loan Reference"
              onChange={(e) => setSearchLoanReference(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All</MenuItem>
              {loans.map((loan) => (
                <MenuItem key={loan.loanId} value={loan.loanReference}>{loan.loanReference}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Due Date From"
              value={searchDueDateFrom}
              onChange={(date) => setSearchDueDateFrom(date)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2, height: 40 } } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Due Date To"
              value={searchDueDateTo}
              onChange={(date) => setSearchDueDateTo(date)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2, height: 40 } } }}
            />
          </LocalizationProvider>
          <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={searchStatus}
              label="Status"
              onChange={(e) => setSearchStatus(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Overdue">Overdue</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="Processing">Processing</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Scheduled">Scheduled</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
              <MenuItem value="Reversed">Reversed</MenuItem>
              <MenuItem value="In Review">In Review</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            size="medium"
            sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ClearIcon />}
            onClick={() => { setSearchLoanReference(''); setSearchDueDateFrom(null); setSearchDueDateTo(null); setSearchStatus(''); }}
            size="medium"
            sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
          >
            Reset
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)', background: '#fff', mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Loan Reference</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Borrower</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Bank</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Loan Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Principal Outstanding</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Interest Outstanding</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map((loan) => (
                <TableRow 
                  key={loan.loanId}
                  sx={{
                    '&:hover': {
                      bgcolor: '#F5F6FF',
                    },
                  }}
                >
                  <TableCell sx={{ color: '#374151' }}>{loan.loanReference}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>{loan.borrower}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>{loan.bank}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>{loan.loanType}</TableCell>
                  <TableCell sx={{ color: '#374151' }}>
                    {loan.totalPrincipalOutstanding.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TableCell>
                  <TableCell sx={{ color: '#374151' }}>
                    {loan.totalInterestOutstanding.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => {
                        setSelectedLoan(loan);
                        handleOpenDialog('view');
                      }}
                      sx={{ color: '#6366F1' }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedLoan && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ color: '#374151', fontWeight: 600, mb: 2 }}>
              Repayment Schedule for {selectedLoan.loanReference}
            </Typography>
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Payment ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Principal Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Interest Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Total Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSchedules.map((schedule, idx) => (
                    <TableRow
                      key={schedule.paymentId}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#F5F6FF' },
                        borderBottom: idx !== paginatedSchedules.length - 1 ? '1px solid #E5E7EB' : 'none',
                        transition: 'background 0.2s',
                        height: 36
                      }}
                    >
                      <TableCell sx={{ py: 0.5 }}>{schedule.paymentId}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{schedule.paymentDueDate.toLocaleDateString()}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{schedule.principalAmount.toLocaleString()}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{schedule.interestAmount.toLocaleString()}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{schedule.totalPayment.toLocaleString()}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{schedule.status}</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{/* Actions */}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <TablePagination
          component="div"
          count={filteredSchedules.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            }
          }}
        >
          <DialogTitle sx={{ 
            color: '#374151',
            fontWeight: 600,
            borderBottom: '1px solid #E5E7EB',
            pb: 2
          }}>
            {dialogType === 'view' && 'View Payment Details'}
            {dialogType === 'record' && 'Record Payment'}
            {dialogType === 'reschedule' && 'Reschedule Payment'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedPayment && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Payment ID"
                    value={selectedPayment.paymentId}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Payment Due Date"
                    value={selectedPayment.paymentDueDate}
                    disabled={dialogType !== 'reschedule'}
                    onChange={(date) => dialogType === 'reschedule' && setFormData({ ...formData, newDueDate: date })}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Principal Amount"
                    value={selectedPayment.principalAmount.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Interest Amount"
                    value={selectedPayment.interestAmount.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Total Payment"
                    value={selectedPayment.totalPayment.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                    }}
                  />
                </Grid>
                {dialogType === 'record' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Actual Payment Date"
                        value={formData.actualPaymentDate}
                        onChange={(date) => setFormData({ ...formData, actualPaymentDate: date })}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6366F1',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Principal Paid"
                        type="number"
                        value={formData.principalPaid}
                        onChange={(e) => setFormData({ ...formData, principalPaid: e.target.value })}
                        fullWidth
                        inputProps={{ step: "0.01" }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6366F1',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Interest Paid"
                        type="number"
                        value={formData.interestPaid}
                        onChange={(e) => setFormData({ ...formData, interestPaid: e.target.value })}
                        fullWidth
                        inputProps={{ step: "0.01" }}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6366F1',
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    multiline
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                color: '#374151',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#F5F6FF',
                },
              }}
            >
              Cancel
            </Button>
            {(dialogType === 'record' || dialogType === 'reschedule') && (
              <Button 
                onClick={handleSave} 
                variant="contained"
                sx={{
                  bgcolor: '#6366F1',
                  '&:hover': {
                    bgcolor: '#4F46E5',
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default RepaymentSchedule; 